import Button from '@mui/material/Button';
import {AccordionSummary, AppBar, Autocomplete, FormControl, InputLabel} from '@mui/material';
import React, {ReactElement, useEffect, useState} from 'react';
import Stack from '@mui/material/Stack';
import Select, {SelectChangeEvent} from '@mui/material/Select';
import {useWorkspaceContext} from '../../../../../context/workspaceContext';
import {uuid} from '../../../../../utils/utils';
import NewOccurrenceReportService from '../../../../../services/new-occurrence.service';
import IRPService from '../../../../../services/irp.service';

import {toast} from 'react-toastify';
import EventSequence from '../../../../../@types/event-sequence';
import {CanvasActionType} from '../../../../../@types/canvas-action-type';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import SpringDataPage from '../../../../../@types/spring-data-page';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import FilterIcon from '@mui/icons-material/FilterAlt';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import {debounce} from '@mui/material/utils';
import ActionDropdown from '../../../../form-components/ActionDropdown.component';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import * as CanvasUtils from '../../../../../utils/canvas/node-utils';
import Skeleton from '@mui/material/Skeleton';
import Toolbar from '@mui/material/Toolbar';
import CloseIcon from '@mui/icons-material/HighlightOff';
import {
    IntegratedRiskPicture, IRPReportProcessingStatus, PagedMappingResponse, ReportMappingGroupDTO
} from '../../../../../@types/integrated-risk-picture';
import {SimpleLinkedOccurrenceDna, SimpleLinkedOccurrenceDnaElement} from '../../../../../@types/mapping-dna';
import NewOccurrenceReport from '../../../../../@types/new-occurrence-report';
import StrictDeleteModal from '../../../../form-components/StrictDeleteModal.component';
import {ResourceType} from '../../../../../@types/resource-type';

interface Column {
    id: 'linked' | 'actions' | 'source' | 'name' | 'location' | 'summary'  | 'datetime' ;
    label: string;
    minWidth?: number;
    align?: 'right';
    format?: (value: number) => string;
}

const columns: readonly Column[] = [
    { id: 'linked', label: 'Linked', minWidth: 30 },
    { id: 'actions', label: 'Actions', minWidth: 100 },
    { id: 'source', label: 'Source', minWidth: 100 },
    { id: 'name', label: 'Name', minWidth: 200 },
    { id: 'location', label: 'Location', minWidth: 70 },
    { id: 'summary', label: 'Summary', minWidth: 200 },
    { id: 'datetime', label: 'DateTime', minWidth: 100 },
];

const occurrenceStatusList: Array<IRPReportProcessingStatus> = [
    'PENDING', 'PROCESSING', 'MAPPED', 'MAPPED_INCOMPLETE', 'NOT_APPLICABLE', 'TECHNICAL_ERROR', 'INSUFFICIENT_DATA'
]

const tooltips: Record<IRPReportProcessingStatus, string> = {
    'PENDING': 'The occurrence is not sentenced yet.',
    'PROCESSING': 'The occurrence is currently being analyzed.',
    'MAPPED': 'The occurrence has been mapped.',
    'MAPPED_INCOMPLETE': 'The occurrence was partially mapped and requires human review.',
    'NOT_APPLICABLE': 'The occurrence is not applicable to this Safety View.',
    'TECHNICAL_ERROR': 'There was a technical error with the occurrence.',
    'INSUFFICIENT_DATA': 'There is insufficient data for the occurrence.'
}

function OccurrenceTable(props: {
    eventSequence: EventSequence,
    selectedOccurrences: Array<number>,
    onSelectChanged: (occurrence: NewOccurrenceReport) => void,
    getDna: (occurrenceId: number) => void,
    saveDna: (occurrenceId: number) => void
}): ReactElement {
    const { eventSequence, selectedOccurrences, onSelectChanged , getDna, saveDna} = props;
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    const [occurrences, setOccurrences] = React.useState<SpringDataPage<NewOccurrenceReport> | null>(null);
    const [searchTerm, setSearchTerm] = React.useState<string | null>(null);
    const [isRunning, setIsRunning] = React.useState<boolean>(false);
    const uuidRef = React.useRef(uuid());

    const debounceFetch = React.useMemo(
        () =>
            debounce(
                (
                    request: { input: string },
                    callback: (results: SpringDataPage<NewOccurrenceReport>) => void,
                ) => {
                    const fetchOccurrences = async (searchTerm: string): Promise<any> => {
                        try {
                            setIsRunning(true);
                            let newPageObj = {
                                offset: 0,
                                size: rowsPerPage,
                                direction: 'ASC',
                                sort: 'datetime',
                            };

                            const myOccurrences: SpringDataPage<NewOccurrenceReport> = await NewOccurrenceReportService.fetchOccurrences(newPageObj, eventSequence.group.id!, searchTerm);
                            callback(myOccurrences);
                        }
                        catch (error: any) {
                            toast.error('Could not fetch occurrences to choose from - ' + error.message);
                        } finally {
                            setIsRunning(false);
                        }
                    }

                    fetchOccurrences(request.input);
                },
                400,
            ),
        [],
    );


    const handleChangePage = (_event: unknown, newPage: number): void => {
        setIsRunning(true);
        let newPageObj = {
            offset: (newPage - 1) * rowsPerPage,
            size: rowsPerPage,
            direction: 'ASC',
            sort: 'datetime',
        }

        NewOccurrenceReportService.fetchOccurrences(newPageObj, eventSequence.group.id!, searchTerm ?? '').then((response) => {
            setPage(newPage);
            setOccurrences(response);
        }).catch((error: Error) => {
            toast.error(error.message);
        }).finally(() => {
            setIsRunning(false);
        });

    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setIsRunning(true);
        let newPageObj = {
            offset: 0,
            size: +event.target.value,
            direction: 'ASC',
            sort: 'datetime',
        }
        NewOccurrenceReportService.fetchOccurrences(newPageObj, eventSequence.group.id!, searchTerm ?? '').then((response) => {
            setRowsPerPage(+event.target.value);
            setPage(0);
            setOccurrences(response);
        }).catch((error: Error) => {
            toast.error(error.message);
        }).finally(() => {
            setIsRunning(false);
        });
    };

    const onSearchTermChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setSearchTerm(event.target.value);
    };

    // add debounceFetch to useEffect
    useEffect(() => {
        if (searchTerm) {
            debounceFetch({ input: searchTerm }, (results: SpringDataPage<NewOccurrenceReport>) => {

                setOccurrences(results);
            });
        }
    }, [searchTerm, debounceFetch]);


    useEffect(() => {
        setIsRunning(true);
        let newPageObj = {
            offset: page * rowsPerPage,
            size: rowsPerPage,
            direction: 'ASC',
            sort: 'datetime',
        }

        NewOccurrenceReportService.fetchOccurrences(newPageObj, eventSequence.group.id!, '').then((response) => {
            setOccurrences(response);
        }).catch((error: Error) => {
            toast.error(error.message);
        }).finally(() => {
            setIsRunning(false);
        });
    }, [eventSequence]);

    return (
        <Paper sx={{ width: '100%', overflow: 'hidden' , mt: 1}}>
            <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isRunning}> 
                <CircularProgress color='inherit' />
            </Backdrop>

            <Box sx={{ justifyContent: 'space-between' }}>
                <TextField
                    label='Filter Occurrences'
                    aria-label='search field'
                    type='text'
                    variant='outlined'
                    size='small'
                    id={`filter-box-${uuidRef.current}`}
                    onChange={onSearchTermChange}
                    sx={{
                        ml: 1, mr: 1, mt: 1,
                    }}

                    slotProps={{
                        input: {
                            'role': 'filterbox',
                            'tabIndex': 0,
                            'aria-labelledby': `filter-box-${uuidRef.current}`,
                            'aria-label': 'filter',
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton aria-label='filter icon' onClick={():void  => {}}>
                                        <FilterIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }
                    }}
                />

                <TablePagination
                    rowsPerPageOptions={[10, 25, 100]}
                    component="div"
                    count={occurrences ? occurrences.page.totalElements  : 0}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    style={{display: 'flex', justifyContent: 'start'}}
                />
            </Box>

            <TableContainer sx={{ minHeight: 200 }}>
                <Table stickyHeader aria-label="sticky table" size='small'>
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell
                                    key={column.id}
                                    align={column.align}
                                    style={{ minWidth: column.minWidth }}
                                >
                                    {column.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {occurrences?.content.map((row: NewOccurrenceReport) => {
                            const isItemSelected = selectedOccurrences.findIndex((id) => id === row.id) !== -1;
                            return (
                                <TableRow
                                    key={row.id}
                                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                >
                                    <TableCell>
                                        <Checkbox
                                            checked={isItemSelected}
                                            inputProps={{ 'aria-label': 'Select' }}
                                            onClick={(): void => onSelectChanged(row)}
                                            disableRipple
                                            disabled={CanvasUtils.canAddOrEditResource(eventSequence) ? false : true}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <ActionDropdown
                                            onClick={(): void => { }}
                                            actions1={[
                                                { name: 'Fetch Mapping', action: (): void => getDna(row.id), disabled: !isItemSelected },
                                                { name: 'Save Mapping', action: (): void => saveDna(row.id), disabled: (!isItemSelected || !CanvasUtils.canAddOrEditResource(eventSequence))},
                                                { name: 'Add To Mappings', action: (): void => onSelectChanged(row), disabled: (isItemSelected || !CanvasUtils.canAddOrEditResource(eventSequence)) },
                                                { name: 'Remove from Mappings', action: (): void =>  onSelectChanged(row), disabled: (!isItemSelected || !CanvasUtils.canAddOrEditResource(eventSequence))},
                                            ]}
                                            actions2={[
                                                { name: 'View Report', action: (): void => {}, disabled: false },
                                            ]}
                                            label=''/>
                                    </TableCell>
                                    <TableCell>
                                        {row.sourceName}
                                    </TableCell>
                                    <TableCell>
                                        {row.uniqueId}
                                    </TableCell>
                                    <TableCell>
                                        {row.facilityName}
                                    </TableCell>
                                    <TableCell>
                                        {row.summary}
                                    </TableCell>
                                    <TableCell>
                                        {row.dateTime.toString() ?? '---'}
                                    </TableCell>

                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
}

export default function PanelDna(props: { eventSequence: EventSequence, isActive: boolean, onDnaToggle: (openDnaPanel: boolean | string[]) => void }): ReactElement {
    const { eventSequence, isActive, onDnaToggle } = props;
    
    const { currentCanvasAction, setCurrentCanvasAction } = useWorkspaceContext();
    const [currentOccurrenceStatus, setCurrentOccurrenceStatus] = useState<string>(occurrenceStatusList[0]);
    const [open, setOpen] = React.useState(false);
    const [expanded, setExpanded] = React.useState<string | false>(false);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [eventSequenceLinkedIrps, setEventSequenceLinkedIrps] = React.useState<Array<IntegratedRiskPicture>>([]);
    const [mappings, setMappings] = useState<PagedMappingResponse | null>(null);
    const [currentLinkedIrp, setCurrentLinkedIrp] = useState<IntegratedRiskPicture | null>(null);
    const [currentMappedReport, setCurrentMappedReport] = useState<ReportMappingGroupDTO | null>(null);

    // Unlink confirmation modal state
    const [unlinkModalOpen, setUnlinkModalOpen] = useState(false);
    const [reportToUnlink, setReportToUnlink] = useState<NewOccurrenceReport | null>(null);

    const uuidRef = React.useRef(uuid());

    const refetchMapping = (reportProcessingId: number): void => {
        let findMapping = mappings?.content.find((item) => item.reportProcessingId === reportProcessingId) || null;
        if (!findMapping) {
            return;
        }
        setLoading(true);
        IRPService.getReportMappings(currentLinkedIrp!.id, reportProcessingId).then((response) => {
            // getReportMappings returns ReportMappingDTO instead of ReportMappingGroupDTO so we need to merge it with the existing mapping
            let updatedMapping = Object.assign({}, findMapping, response);
            // replace the existing mapping with the updated one
            let updatedMappings = mappings?.content.map((item) => item.reportProcessingId === reportProcessingId ? updatedMapping : item) || [];
            setMappings({ ...mappings!, content: updatedMappings });
            setCurrentMappedReport(updatedMapping);
            setCurrentOccurrenceStatus(updatedMapping.processingStatus);
            drawMapping(updatedMapping);
        }).catch((error: Error) => {
            toast.error(error.message);
        }).finally(() => {
            setLoading(false);
        });
    }

    const saveDNA = (occurrenceId: number): void => {
        setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_STATUS_CHANGE_ODNA, query: occurrenceId });
    }


    useEffect(() => {
        if (isActive && currentCanvasAction !== null && currentCanvasAction.actionType !== CanvasActionType.UNKNOWN) {
            switch (currentCanvasAction.actionType) {
                case CanvasActionType.CANVAS_TOGGLE_ODNA:
                    setTimeout(() => onDnaToggle(!open));
                    setOpen(!open);
                    if (open) {
                        setExpanded(false);
                        setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_PULLED_ODNA, query: null });
                    }
                    break;
                default:
                    break;
            }
            setTimeout(() => {
                setCurrentCanvasAction({ actionType: CanvasActionType.UNKNOWN });
            }, 100);

        }

    }, [currentCanvasAction, isActive]);

    useEffect(() => {
        if (!eventSequence || !eventSequence.events || !open) return;
        setExpanded(false);
        let linkedIrpsPromise = IRPService.getLinkedIrpsForEventSequence(eventSequence.id);
        setLoading(true);
        Promise.all([linkedIrpsPromise]).then((responses) => {
            setEventSequenceLinkedIrps(responses[0]);
            setCurrentLinkedIrp(null);
            setMappings(null);
            setCurrentMappedReport(null);
            setCurrentOccurrenceStatus(occurrenceStatusList[0]);
            // eslint-disable-next-line no-console
            console.log('Linked IRPs', responses[0]);
        }).catch((error: Error) => {
            toast.error(error.message);
        }).finally(() => {
            setLoading(false);
        });
        
    }, [open]);


    useEffect(() => {
        if (currentLinkedIrp) {
            setLoading(true);
            IRPService.getIrpMappings(currentLinkedIrp.id, 0, 100).then((response) => {
                setMappings(response);
                setCurrentMappedReport(null);
                setCurrentOccurrenceStatus(occurrenceStatusList[0]);
                // eslint-disable-next-line no-console
                console.log('Mappings for selected report', response);
            }).catch((error: Error) => {
                toast.error(error.message);
            }).finally(() => {
                setLoading(false);
            });
        }
    }, [currentLinkedIrp]);

    // onLinkedIrpChange removed, now handled by Autocomplete

    const drawMapping = (mapping: ReportMappingGroupDTO | null): void => {
        if (mapping) {
            let events: SimpleLinkedOccurrenceDnaElement[] = mapping.nodeMappings?.filter((item) => item.nodeType !== 'FAULT_TREE_NODE').map((item) => ({ id: item.nodeId, justification: item.justification ?? '', failureStatus: item.positiveInteraction ? 'SUCCESS' : 'FAILURE', relevant: item.relevant ?? true })) || [];  
            let faultTreeNodes: SimpleLinkedOccurrenceDnaElement[] = mapping.nodeMappings?.filter((item) => item.nodeType === 'FAULT_TREE_NODE').map((item) => ({ id: item.nodeId, justification: item.justification ?? '', failureStatus: item.positiveInteraction ? 'SUCCESS' : 'FAILURE', relevant: item.relevant ?? true })) || [];
            let dna: SimpleLinkedOccurrenceDna = {
                irpId: currentLinkedIrp!.id,
                occurrenceId: mapping.reportId,
                reportProcessingId: mapping.reportProcessingId!,
                occurrenceStatus: mapping.processingStatus,
                events: events,
                faultTreeNodes: faultTreeNodes,
                eventSequenceId: eventSequence.id,
                eventIds: events.map((item) => item.id),
                faultTreeNodeIds: faultTreeNodes.map((item) => item.id)
            };
            setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_PULLED_ODNA, query: dna });
        }
    };

    const onMappedReportStatusChange = (event: SelectChangeEvent): void => {
        // eslint-disable-next-line no-console
        console.log('Selected status', event.target.value);
        setCurrentOccurrenceStatus(event.target.value);
        setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_STATUS_CHANGE_ODNA, query: event.target.value });
    };


    const onOccurrenceTableSelectChanged = (occurrence: NewOccurrenceReport): void => {
        // eslint-disable-next-line no-console
        console.log('Occurrence selected/deselected', occurrence);
        let linked = mappings?.content.findIndex((item) => item.reportId === occurrence.id) !== -1;
        if (!linked) {
            // Linking doesn't require confirmation
            IRPService.linkReportToIrp(currentLinkedIrp!.id, eventSequence.id, occurrence.id).then((response) => {
                setCurrentLinkedIrp(response);
                toast.success(`Occurrence ${occurrence.uniqueId} linked to IRP ${currentLinkedIrp!.name}`);
            }).catch((error: Error) => {
                toast.error(error.message);
            });
        } else {
            // Unlinking requires confirmation - show modal
            setReportToUnlink(occurrence);
            setUnlinkModalOpen(true);
        }
    }

    // Close unlink confirmation modal
    const handleUnlinkModalClose = (): void => {
        setUnlinkModalOpen(false);
        setReportToUnlink(null);
    };

    // Execute the actual unlink after confirmation
    const handleUnlinkConfirmed = (): void => {
        if (!reportToUnlink || !currentLinkedIrp) return;

        IRPService.unlinkReportFromIrp(currentLinkedIrp.id, eventSequence.id, reportToUnlink.id).then((response) => {
            setCurrentLinkedIrp(response);
            toast.success(`Occurrence ${reportToUnlink.uniqueId} unlinked from IRP ${currentLinkedIrp.name}`);
        }).catch((error: Error) => {
            toast.error(error.message);
        }).finally(() => {
            handleUnlinkModalClose();
        });
    };


    const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean): void => {
        setExpanded(isExpanded ? panel : false);
    };

    if (open === false) {
        return <></>;
    }


    return loading ?
        <Stack spacing={1}>
            <Skeleton variant='rectangular' width='100%' height={40} animation="wave"/>
            <Skeleton variant='rectangular' width='100%' height={80} animation="wave"/>
            <Skeleton variant='rectangular' width='100%' height={80} animation="wave"/>
        </Stack> : (
            <Paper sx={{ height: '100%', overflow: 'auto' }}>
                <Box sx={{flexGrow: 1}}>
                    <AppBar position="static" color="primary">
                        <Toolbar>
                            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                                Report Mapping Panel
                            </Typography>
                            <Box sx={{ flexGrow: 1 }} />

                            <IconButton
                                color="inherit"
                                component='button'
                                edge='end'
                                onClick={(): void => setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_TOGGLE_ODNA })}>
                                <CloseIcon sx={{width: 25, height: 25}}/>
                            </IconButton>
                        </Toolbar>
                    </AppBar>
                </Box>
                <Stack spacing={2} justifyContent='start' sx={{ m: 1 }}>
                    <FormControl sx={{ minWidth: 270 }}>
                        <Autocomplete
                            id={`linked-irps-autocomplete-${uuidRef.current}`}
                            options={eventSequenceLinkedIrps}
                            getOptionLabel={(option: IntegratedRiskPicture): string => `${option.name} (${option.status})`}
                            value={currentLinkedIrp}
                            onChange={(_event: React.SyntheticEvent, newValue: IntegratedRiskPicture | null): void => {
                                if (newValue) {
                                    setCurrentLinkedIrp(newValue);
                                    setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_IRP_SELECT, query: newValue });
                                }
                            }}
                            renderInput={(params): React.ReactElement => (
                                <TextField
                                    {...params}
                                    label="Associated IRPs"
                                    size="small"
                                />
                            )}
                            isOptionEqualToValue={(option: IntegratedRiskPicture, value: IntegratedRiskPicture): boolean => option.id === value.id}
                        />
                    </FormControl>
                    {mappings && <FormControl sx={{ minWidth: 270 }}>
                        <Autocomplete
                            id={`mapping-irp-autocomplete-${uuidRef.current}`}
                            options={mappings.content}
                            getOptionLabel={(option: ReportMappingGroupDTO): string => `${option.reportIdentifier} (${option.processingStatus})`}
                            value={currentMappedReport}
                            onChange={(_event: React.SyntheticEvent, newValue: ReportMappingGroupDTO | null): void => {
                                if (newValue) {
                                    setCurrentMappedReport(newValue);
                                    setCurrentOccurrenceStatus(newValue.processingStatus);
                                    drawMapping(newValue);
                                }
                            }}
                            renderInput={(params): React.ReactElement => (
                                <TextField
                                    {...params}
                                    label="Mapping Reports"
                                    size="small"
                                />
                            )}
                            isOptionEqualToValue={(option: ReportMappingGroupDTO, value: ReportMappingGroupDTO): boolean => option.reportProcessingId === value.reportProcessingId}
                        />
                        {/* Pagination controls for mappings */}
                        <Box display="flex" justifyContent="center" alignItems="center" mt={1}>
                            <Button
                                size="small"
                                onClick={(): void => {
                                    if (!currentLinkedIrp || !mappings) return;
                                    const prevPage = Math.max(0, mappings.pageNumber - 1);
                                    setLoading(true);
                                    IRPService.getIrpMappings(currentLinkedIrp.id, prevPage, mappings.pageSize)
                                        .then((response) => {
                                            setMappings(response);
                                        })
                                        .catch((error: Error) => {
                                            toast.error(error.message);
                                        })
                                        .finally(() => {
                                            setLoading(false);
                                        });
                                }}
                                disabled={mappings.pageNumber === 0}
                            >Prev</Button>
                            <Typography variant="caption" sx={{ mx: 1 }}>
                                Page {mappings.pageNumber + 1} / {Math.max(1, Math.ceil(mappings.totalElements / mappings.pageSize))}
                            </Typography>
                            <Button
                                size="small"
                                onClick={(): void => {
                                    if (!currentLinkedIrp || !mappings) return;
                                    const nextPage = mappings.pageNumber + 1;
                                    const maxPage = Math.ceil(mappings.totalElements / mappings.pageSize) - 1;
                                    if (nextPage > maxPage) return;
                                    setLoading(true);
                                    IRPService.getIrpMappings(currentLinkedIrp.id, nextPage, mappings.pageSize)
                                        .then((response) => {
                                            setMappings(response);
                                        })
                                        .catch((error: Error) => {
                                            toast.error(error.message);
                                        })
                                        .finally(() => {
                                            setLoading(false);
                                        });
                                }}
                                disabled={mappings.pageNumber >= Math.ceil(mappings.totalElements / mappings.pageSize) - 1}
                            >Next</Button>
                        </Box>
                    </FormControl>}
                    
                    {currentMappedReport && <Stack direction="row" spacing={2} justifyContent='start' sx={{ m: 1 }}>
                        
                        <FormControl size='small' sx={{ minWidth: 200 }}>
                            <InputLabel tabIndex={-1} id={`occurrence-status-label-${uuidRef.current}`} htmlFor={`occurrence-status-list-${uuidRef.current}`}>Mapping Status</InputLabel>
                            <Select
                                labelId={`occurrence-status-label-${uuidRef.current}`}
                                id={`occurrence-status-list-${uuidRef.current}`}
                                value={currentOccurrenceStatus}
                                label="Mapping Status"
                                onChange={onMappedReportStatusChange}
                                inputProps={{
                                    'role': 'textbox',
                                    'tabIndex': 0,
                                    'id': `occurrence-status-list-${uuidRef.current}`,
                                }}
                            >
                                {occurrenceStatusList.map((status) => (
                                    <MenuItem key={status} value={status} title={tooltips[status]}>{status}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        

                        {currentMappedReport && <Button variant="contained" color="primary" size='small' onClick={(): void => {refetchMapping(currentMappedReport.reportProcessingId)}}>Get Mapping</Button>}
                        {currentMappedReport && <Button variant="contained" color="primary" size='small' onClick={(): void => setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_CLEAR_ODNA })}>Clear Mapping</Button>}

                    </Stack>}

                </Stack>
                
                {currentLinkedIrp && <Stack direction="column" spacing={2} justifyContent='start' sx={{ m: 1 }}>
                    <Accordion disableGutters expanded={expanded ==='occurrence-management'} onChange={handleAccordionChange('occurrence-management')} disabled={!CanvasUtils.canAddOrEditResource(eventSequence)}>
                        <AccordionSummary
                            aria-controls="Statistics-content"
                            id={`odna-header-${uuidRef.current}`}
                            expandIcon={<ExpandMoreIcon />}
                        >
                            <Typography variant="h6" component="div" color='primary' sx={{ mr: 2, mt: 0.5 }}>Occurrences Management</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                    
                            <OccurrenceTable eventSequence={eventSequence}
                                selectedOccurrences={mappings?.content.map((item) => item.reportId) || []}
                                onSelectChanged={onOccurrenceTableSelectChanged}
                                getDna={refetchMapping}
                                saveDna={saveDNA}
                            />

                        </AccordionDetails>
                    </Accordion>
                    {/*
                    {esdPsfTemplate && <Accordion disableGutters expanded={expanded === 'psf-selection'} onChange={handleAccordionChange('psf-selection')}>
                        <AccordionSummary
                            aria-controls="PSF-content"
                            id={`panel1a-header-${uuidRef.current}`}
                            expandIcon={<ExpandMoreIcon />}
                        >
                            <Typography variant="h6" component="div" color='primary'>Performance Shaping Factors</Typography>
                        </AccordionSummary>
                        <AccordionDetails >
                            <Autocomplete
                                options={esdPsfTemplate && esdPsfTemplate.psfTemplateItems ? esdPsfTemplate.psfTemplateItems.sort((a, b) => a.category.localeCompare(b.category)) : []}
                                getOptionLabel={(psfItem: PsfTemplateItem): string => psfItem.code ? `${psfItem.name} - ${psfItem.code}` : psfItem.name}
                                renderOption={(props, option: PsfTemplateItem): ReactElement => (
                                    <li {...props} key={option.id}>
                                        {option.code ? `${option.name} - ${option.code}` : option.name}
                                    </li>
                                )}
                                renderTags={(tagValue: PsfTemplateItem[], getTagProps): any => {
                                    return tagValue.map((option, index) => (
                                        <Chip
                                            label={option.code ? `${option.name} - ${option.code}` : option.name}
                                            {...getTagProps({ index })} key={option.id}
                                        />
                                    ))
                                }}
                                groupBy={(option: PsfTemplateItem): string => option.category}
                                renderInput={(params): any => <TextField {...params} label="PSF Code" />}
                                onChange={(event: any, newValue: PsfTemplateItem[]): void => {
                                    setSelectedPsfItems(newValue);
                                }}
                                limitTags={6}
                                multiple
                                filterSelectedOptions
                            />
                            <Autocomplete
                                options={linkedOccurrences}
                                getOptionLabel={(option): string => option.displayName}
                                renderInput={(params): any => <TextField {...params} label="Occurrence Reports" />}
                                renderOption={(props, option: BasicLinkedOccurrence): ReactElement => (
                                    <li {...props} key={option.id}>
                                        {option.displayName}
                                    </li>
                                )}
                                renderTags={(tagValue: BasicLinkedOccurrence[], getTagProps): any => {
                                    return tagValue.map((option, index) => (
                                        <Chip
                                            label={option.displayName}
                                            {...getTagProps({ index })} key={option.id}
                                        />
                                    ))
                                }}
                                onChange={(event: any, newValue: BasicLinkedOccurrence[]): void => {
                                    setSelectedPsfOccurrenceReports(newValue);
                                }}
                                limitTags={6}
                                multiple
                                filterSelectedOptions
                                disableCloseOnSelect
                                sx={{ mt: 2 }}
                            />
                        </AccordionDetails>
                    </Accordion>
                    */}


                </Stack>}

                {/* Unlink Confirmation Modal */}
                {reportToUnlink && (
                    <StrictDeleteModal
                        open={unlinkModalOpen}
                        resourceType={ResourceType.NEW_OCCURRENCE_REPORTS}
                        resourceName={reportToUnlink.uniqueId || `Report #${reportToUnlink.id}`}
                        subResources="its mappings to fault tree nodes"
                        actionLabel="Unlink"
                        onClose={handleUnlinkModalClose}
                        handleDelete={handleUnlinkConfirmed}
                    />
                )}

            </Paper>
        );
}
